import React, { useState, useEffect } from "react";
import { abbreviateNumber } from "js-abbreviation-number";
import { Link } from "react-router-dom";
import slugify from "../utils/slugify";
import LazyImage from "./LazyImage";
import VideoLength from "../shared/VideoLength";
import { addToFavorites, removeFromFavorites, isFavorited } from "../utils/favorites";
import { MdBookmarkBorder, MdBookmark } from "react-icons/md";

const SearchResultVideoCard = ({ video }) => {
  const slug = slugify(video?.title || video?.videoId || "video");
  
  // Determine thumbnail URL based on platform
  const getThumbnailUrl = () => {
    // First try the video's own thumbnail
    if (video?.thumbnails?.[0]?.url) return video.thumbnails[0].url;
    if (video?.thumbnail) return video.thumbnail;
    
    // For YouTube videos, use YouTube thumbnail
    if (video?.platform === 'youtube' || video?.source === 'youtube') {
      const ytId = video?.videoId;
      if (ytId && ytId.length === 11) {
        return `https://i.ytimg.com/vi/${ytId}/hqdefault.jpg`;
      }
    }
    
    // For Facebook videos without thumbnail, return null (will be filtered out)
    if (video?.platform === 'facebook' || video?.source === 'facebook') {
      return null;
    }
    
    // Default fallback
    return '/default-thumbnail.svg';
  };
  
  const thumbnailUrl = getThumbnailUrl();
  const [favorited, setFavorited] = useState(false);

  // Debug log to see what video object is being passed
  console.log('[SearchResultVideoCard] video:', { 
    platform: video?.platform, 
    source: video?.source, 
    url: video?.url,
    originalUrl: video?.originalUrl,
    embed: video?.embed,
    title: video?.title,
    thumbnail: thumbnailUrl
  });

  useEffect(() => {
    const id = video?.videoId || video?.originalUrl || video?.url || null;
    if (id) setFavorited(isFavorited(id));
  }, [video]);

  const onToggleFavorite = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const id = video?.videoId || video?.originalUrl || video?.url || null;
    if (!id) return;
    if (favorited) {
      removeFromFavorites(id);
      setFavorited(false);
    } else {
      addToFavorites(video);
      setFavorited(true);
    }
  };
  
  // Don't render if no valid thumbnail (for Facebook videos without thumbnails)
  if (!thumbnailUrl || thumbnailUrl === '/default-thumbnail.svg') {
    console.log('[SearchResultVideoCard] Skipping video without thumbnail:', video?.title);
    return null;
  }
  
  return (
    // Pass the full video object into route state so the details page
    // can render an embed player even for search/deep results that
    // are not stored in Firebase.
    <Link to={`/${slug}`} state={{ playerVideo: video }}>
      <div className="flex flex-col md:flex-row mb-8 md:mb-3 lg:hover:bg-black/[0.1] dark:lg:hover:bg-white/[0.1] rounded-xl md:p-4">
        <div className="relative flex shrink-0 h-48 md:h-28 lg:h-40 xl:h-48 w-full md:w-48 lg:w-64 xl:w-80 rounded-xl bg-slate-800 overflow-hidden">
          <LazyImage
            src={thumbnailUrl}
            alt="thumbnails"
            className="h-full w-full object-fill"
            videoUrl={video?.originalUrl || video?.thumbnails?.[0]?.url}
            videoDuration={video?.lengthSeconds}
          />

          {/* Favorite button overlay */}
          <button
            title={favorited ? 'Saved' : 'Save'}
            onClick={onToggleFavorite}
            className="absolute right-2 top-2 z-20 text-white bg-black/30 hover:bg-black/50 p-1 rounded-full"
          >
            {favorited ? <MdBookmark className="text-lg" /> : <MdBookmarkBorder className="text-lg" />}
          </button>

          {video?.lengthSeconds && <VideoLength time={video?.lengthSeconds} />}
        </div>

        <div className="flex flex-col ml-4 md:ml-6 mt-4 md:mt-0 overflow-hidden">
          <span className="text-base md:text-lg lg:text-2xl font-semibold line-clamp-2 text-black dark:text-[#ebedeeed] whitespace-normal break-words">
            {video?.title}
          </span>

          <span className="empty:hidden text-sm line-clamp-1 md:line-clamp-2 text-black dark:text-white/[0.7] md:pr-24 md:my-4">
            {video?.descriptionSnippet}
          </span>

          <div className="flex text-xs font-semibold text-black dark:text-white/[0.7] truncate overflow-hidden">
            <span>{`${abbreviateNumber(video?.stats?.views, 2)} views`}</span>
            <span className="flex text-[24px] text-black dark:text-white/[0.7] font-bold leading-none relative top-[-10px] mx-1">
              .
            </span>
            <span className="truncate">{video?.publishedTimeText}</span>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default SearchResultVideoCard;
